"use client";

import { useEffect } from "react";
import { capture } from "@/lib/analytics";
import { trackGaEvent } from "@/lib/gtag";
import { readMarketingCampaign, rememberLandingAudience } from "@/lib/signupAttribution";

/** Editor-acquisition events contain route and placement enums only—never prose. */
export function EditorMarketingAnalytics({ page }: { page: string }) {
  useEffect(() => {
    // Reuse signup attribution through internal pages and OAuth. A CTA placement
    // must not overwrite a known campaign; placements remain separate events.
    const source = readMarketingCampaign();
    rememberLandingAudience("editor");
    const properties = { page, audience: "editor", ...(source ? { source } : {}) };
    if (source) {
      for (const anchor of document.querySelectorAll<HTMLAnchorElement>("a[data-editor-cta]")) {
        const url = new URL(anchor.href, window.location.origin);
        if (url.origin === window.location.origin && /\/(?:sign-up)$/.test(url.pathname)) {
          url.searchParams.set("src", source);
          anchor.href = url.toString();
        }
      }
    }
    capture("editor_marketing_viewed", properties);
    trackGaEvent("editor_marketing_viewed", properties);

    function onClick(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      const link = target?.closest<HTMLElement>("[data-editor-cta]");
      if (!link) return;
      const action = link.dataset.editorCta;
      const location = link.dataset.editorCtaLocation;
      if (!action) return;
      capture("editor_marketing_cta_clicked", {
        ...properties,
        action,
        location,
      });
      trackGaEvent("editor_marketing_cta_clicked", {
        ...properties,
        action,
        location,
      });
    }

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, [page]);

  return null;
}
